import type { Theme } from '@/stores/ui.store';

type Hsl = { h: number; s: number; l: number };

type Keyframe = { minute: number; value: Hsl };

const LIGHT_HUE = 50;

const LIGHT_TINT_KEYFRAMES: Keyframe[] = [
  { minute: 0, value: { h: LIGHT_HUE, s: 8, l: 100 } },
  { minute: 6 * 60, value: { h: LIGHT_HUE, s: 14, l: 99 } },
  { minute: 9 * 60, value: { h: LIGHT_HUE, s: 20, l: 98 } },
  { minute: 12 * 60, value: { h: LIGHT_HUE, s: 24, l: 97 } },
  { minute: 15 * 60, value: { h: LIGHT_HUE, s: 27, l: 96 } },
  { minute: 18 * 60, value: { h: LIGHT_HUE, s: 30, l: 95 } },
  { minute: 21 * 60, value: { h: LIGHT_HUE, s: 18, l: 98 } },
  { minute: 24 * 60, value: { h: LIGHT_HUE, s: 8, l: 100 } },
];

const DARK_HUE = 210;

const DARK_TINT_KEYFRAMES: Keyframe[] = [
  { minute: 0, value: { h: DARK_HUE, s: 5, l: 3 } },
  { minute: 6 * 60, value: { h: DARK_HUE, s: 6, l: 5 } },
  { minute: 9 * 60, value: { h: DARK_HUE, s: 8, l: 7 } },
  { minute: 12 * 60, value: { h: DARK_HUE, s: 10, l: 12 } },
  { minute: 15 * 60, value: { h: DARK_HUE, s: 11, l: 12 } },
  { minute: 18 * 60, value: { h: DARK_HUE, s: 12, l: 10 } },
  { minute: 21 * 60, value: { h: DARK_HUE, s: 7, l: 5 } },
  { minute: 24 * 60, value: { h: DARK_HUE, s: 5, l: 3 } },
];

function easeCosine(t: number): number {
  return (1 - Math.cos(Math.PI * t)) / 2;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpHsl(a: Hsl, b: Hsl, t: number): Hsl {
  return {
    h: lerp(a.h, b.h, t),
    s: lerp(a.s, b.s, t),
    l: lerp(a.l, b.l, t),
  };
}

function hslaString({ h, s, l }: Hsl, a = 1): string {
  return `hsla(${h.toFixed(1)}, ${s.toFixed(1)}%, ${l.toFixed(2)}%, ${a})`;
}

function sample(frames: Keyframe[], minute: number): Hsl {
  for (let i = 0; i < frames.length - 1; i++) {
    const a = frames[i];
    const b = frames[i + 1];
    if (minute >= a.minute && minute <= b.minute) {
      const t = easeCosine((minute - a.minute) / (b.minute - a.minute));
      return lerpHsl(a.value, b.value, t);
    }
  }
  return frames[0].value;
}

export type AmbientTint = {
  tint: string;
};

export function getAmbientTint(date: Date, baseTheme: Theme): AmbientTint {
  const minute = date.getHours() * 60 + date.getMinutes();
  const frames =
    baseTheme === 'dark' ? DARK_TINT_KEYFRAMES : LIGHT_TINT_KEYFRAMES;
  return { tint: hslaString(sample(frames, minute), 1) };
}
